import { ExceptionUtils } from "#src/utils/exception";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import { BaseError, downloadLink } from "@validereinc/utilities";

/**
 * Generic hook that wraps an adapter's `exportList` call in a `useMutation`,
 * triggers a file download on success, and surfaces a toast + Sentry report on
 * failure. Replaces the manual `isExporting` state + try/catch/finally pattern
 * that was duplicated across table-panel export handlers.
 *
 * @example
 * const { mutate: handleExport, isLoading: isExporting } = useExportList({
 *   exportFn: () => WorkflowAdapter.exportList!(reqPayload),
 * });
 */
export const useExportList = ({
  exportFn,
  successDescription,
  errorDescription = "Failed to export.",
}: {
  /**
   * An async function that calls the adapter's export endpoint and returns an
   * object with at minimum `s3_download_link`. Include a `name` field to set
   * the downloaded file's name. For custom filenames computed at click-time,
   * return the desired name from this function.
   */
  exportFn: () => Promise<{ s3_download_link: string; name?: string }>;
  /** Optional toast message shown on successful export. */
  successDescription?: string;
  /** Toast error message shown when the export fails. */
  errorDescription?: string;
}) => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const report = await exportFn();

      if (!report?.s3_download_link) {
        throw new BaseError("Could not fetch download URL for export", {
          cause: report,
        });
      }

      downloadLink(report.s3_download_link, report.name);
    },
    onSuccess: () => {
      if (successDescription) {
        toast.push({ intent: "success", description: successDescription });
      }
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({ intent: "error", description: errorDescription });
    },
  });
};
